import React, { Fragment } from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { normalize } from 'styled-normalize';
import { spacing } from '_utils/props';

import {
  buttonColors,
  buttonReset,
  buttonSize,
  focusOutline,
  mq,
  safeFocusRemoval,
  textBody1,
} from '_utils/styles/';

import { ChildrenPropTypes } from '../../../types';
import theme from './theme';

export * from './theme';

const GlobalStyles = createGlobalStyle`

${normalize}

html {
  box-sizing: border-box;
  height: 100%;
}

* {
  &,
  &:before,
  &:after {
    box-sizing: inherit;
  }
}

body {
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  ${textBody1}
  color: ${theme.colors.text.default};
  margin: 0;
  max-width: 100%;
  min-height: 100%;
  min-width: 300px;
  overflow-x: hidden;
  position: relative;
  &.menu-active {
    height: 100%;
  }
}

#root {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
}

img,
picture,
svg {
  vertical-align: middle;
  border-style: none;
  height: auto;
  max-width: 100%;
}

img {
  &.lazyload,
  &.lazyloading  {
    opacity: 0;
  }

  &.lazyloaded {
    ${mq('md')} {
      transition: opacity ${theme.motion.timing.fast} linear;
    }

    opacity: 1;
  }
}

.mobile-app-mode-hide{
  display:none !important;
}
  
.button-primary {
  ${buttonReset()}
  ${buttonSize(theme.button.default)}
  ${buttonColors(theme.colors.button.primary)}
}

.button-ghost {
  ${buttonReset()}
  ${buttonSize(theme.button.default)}
  ${buttonColors(theme.colors.button.ghost)}
}

.button-slim {
  ${buttonSize(theme.button.slim)}
}

a:focus {
  ${safeFocusRemoval}
  ${focusOutline({ theme })}
}

.pageTitle,
h1,
.headingH1,
h2,
.headingH2,
h3,
.headingH3,
h4,
.headingH4,
h5,
.headingH5,
h6,
.headingH6 {
  color: ${theme.colors.all.brandPrimary3};
}

${mq('lg')} {
  .fullWidthOnDesktop .content-rich-text > *  {
    max-width: 100% !important;
  }
}

/* start placeholder spacing for verical rhythm between components */

/*  using 'noindex' DOM element for .coveo-no-index-begin/end which is wrapper to output
    HTML comments - this just to reduce CSS specificity complexity for targeting spacing rules */
noindex {
  display: none;
}

.ph {

  // targeting row as ph too high and cannot check :empty
  .row:not(.db-row):not(:empty) {
    ${({ theme }) => spacing({ theme, py: 7 })}
  }

  &.no-pt {
    .row:not(:empty) {
      padding-top: 0;
    }
  }

  // emulate removing the checkbox for padding on first placeholder after PageBanner (text only)
  .page-banner + .ph.default .row {
    padding-top: 0;
  }

  // fix for 2 siblings same type to make tighter with negative margin
  &.default:not(.is-empty) + &.default:not(.is-empty),
  &.dark:not(.is-empty) + &.dark:not(.is-empty),
  &.grey:not(.is-empty) + &.grey:not(.is-empty),
  // just in case casing mis-match
  &.Dark:not(.is-empty) + &.Dark:not(.is-empty),
  &.Grey:not(.is-empty) + &.Grey:not(.is-empty) {

    .row:not(:empty) {
      padding-top: 0;
      ${({ theme }) => spacing({ theme, mt: -3 })}
    }
  }

  &.no-pb {
    .row:not(:empty) {
      padding-bottom: 0;
    }
  }

  // component level spacing
  .row > [data-component]:not(:only-of-type):not(:last-of-type) {
    ${({ theme }) => spacing({ theme, mb: 6 })}
  }

  // dashboard override to make tighter
  .db-fw &.ph-sc .row {

    > [data-component]:not(:only-of-type):not(:last-of-type) {
      ${({ theme }) => spacing({ theme, mb: 3 })}
    }
  }

}
/* end placeholder spacing */

/* negate top margin on Page Banner if:
    - there is a breadcrumb component on the page and
    - no campaign navigation exists
*/
body:not(.hasCampaignNavigation) .page-breadcrumb ~ .page-banner {
  margin-top: 0;
}

.hide-on-print {
  @media print {
    display: none !important;
  }
}

@media print {
  * {
      background: transparent !important;
      color: black !important;
      color-adjust: economy;
      box-shadow: none !important;
      text-shadow: none !important;

      &::after {
        box-shadow: none !important;
      }
    }

    // Prevent page breaks in the middle of a blockquote or preformatted text block
    pre,
    blockquote {
      border: 1px solid black;
      break-inside: avoid;
    }

    tr,
    img {
      break-before: auto; /* 'always,' 'avoid,' 'left,' 'inherit,' or 'right' */
      break-after: auto; /* 'always,' 'avoid,' 'left,' 'inherit,' or 'right' */
      break-inside: avoid; /* or 'auto' */
     }
    img { max-width: 100% !important; }

    @page { margin: 0.5cm; }

    p,
    h2,
    h3 {
      break-after: avoid;
      orphans: 3;
      widows: 3;
    }

     // hide OneTrust consent & Live Chat when printing
    .chat-iframe-wrap,
    .button-iframe-wrap,
    iframe#icChatButton,
    div#onetrust-consent-sdk  {
      display: none;
    }

    .ph > [data-component] {
      margin-bottom: 20px !important;
    }
  }
`;

export default function Theme({ children }) {
  return (
    <ThemeProvider theme={theme}>
      <Fragment>
        <GlobalStyles />
        {children}
      </Fragment>
    </ThemeProvider>
  );
}

Theme.propTypes = {
  children: ChildrenPropTypes,
};
